import 'apps/admin/styles/components/configuration-manager.scss';
import React from 'react';
import PropTypes from 'prop-types';
import { resolve } from 'library/helpers/path-resolver';
import LoadGraphData from 'util/hoc/with-data';
import Query from 'apps/admin/util/query';
import ConfigSegments from 'apps/admin/components/config/segments';
import Toggle from 'library/components/form-elements/toggle';
import mergeOptions from 'library/util/merge-options';

const Configuration = ({ id, title, config, user_config, org_config, onUpdate, connectable_type, disabled=false }) => {
  const Component = ConfigSegments[title] || ConfigSegments.default;

  return (
    <div className="pairin-admin-configuration">
      <Component
        disabled={disabled}
        config={
          connectable_type==='user'
            ? user_config
            : org_config
        }
        defaultConfig={config}
        userConfig={user_config}
        orgConfig={org_config}
        onUpdate={(value)=>(
          onUpdate(id, value)
        )}
      />
    </div>
  );
};

const TypeToShort = (type) => {
  switch (type) {
  case 'organization':
    return 'org';
  }
  return type;
};

class SegmentCard extends React.Component {
  state = {
    loading: false
  }

  deactivate = async (segment_id) => (
    await Query(`
      mutation {
        disconnect_segment (segment_id: ${segment_id}, connectable_id: ${this.props.connectable_id}, connectable_type: "${this.props.connectable_type}") {
          success
        }
      }
    `)
  )
  activate = async (segment_id) => (
    await Query(`
      mutation {
        update_segment (id: ${segment_id}, connectable_id: ${this.props.connectable_id}, connectable_type: "${this.props.connectable_type}", config:"{}") {
          success
        }
      }
    `)
  )

  handleToggle = async ({ target }) => {
    this.setState({
      loading: true
    });
    try {
      if (!target.value) {
        await this.deactivate(target.name);
      } else {
        await this.activate(target.name);
      }
      await this.props.reload_graph();
    } catch (e) {
      console.error(e);
    }

    this.setState({
      loading: false
    });
  }

  handleConfigUpdate = async (id, value) => {
    this.setState({
      loading: true
    });

    try {
      const currentConfig = this.props[`${TypeToShort(this.props.connectable_type)}_config`]||{};
      const [pre, title] = Object.keys(value)[0].split('-');
      const nestedObjects = ['job_families', 'disabledSteps'];

      if (nestedObjects.includes(pre) && title) {
        const existing = {...currentConfig[`${pre}`]};

        existing[`${title}`] = value[`${pre}-${title}`];
        currentConfig[`${pre}`] = {...existing};
        value = {}
      }

      await Query(`
        mutation {
          update_segment (id: ${id}, connectable_id: ${this.props.connectable_id}, connectable_type: "${this.props.connectable_type}", config:"${JSON.stringify(mergeOptions(currentConfig, value)).replace(/"/g, '\\"')}") {
            success
          }
        }
      `);

      await this.props.reload_graph();
    } catch (e) {
      console.error(e);
    }
    this.setState({
      loading: false
    });
  }

  render() {
    const {
      id,
      title,
      enabled,
      config,
      org_enabled,
      user_enabled,
      user_config,
      org_config,
      connectable_id,
      connectable_type,
      reload_graph
    } = this.props;
    const optionalProps = {};

    if (enabled) {
      optionalProps['data-tooltip'] = 'This is enabled for all users by default!';
    } else if (connectable_type === 'user' && org_enabled) {
      optionalProps['data-tooltip'] = 'This is enabled for the user\'s whole organization!';
    }

    return (
      <div className={['pairin-admin-segment', enabled||user_enabled||org_enabled?'enabled':''].filter(c=>c).join(' ')}>
        <h3>
          <span>{ title }</span>
          <span {...optionalProps}>
            <Toggle
              name={id}
              onChange={this.handleToggle}
              checked={enabled||user_enabled||org_enabled}
              disabled={enabled||(connectable_type === 'user' && org_enabled)||this.state.loading}
            />
          </span>
        </h3>
        <Configuration
          id={id}
          title={title}
          config={config}
          user_config={user_config}
          org_config={org_config}
          connectable_type={connectable_type}
          connectable_id={connectable_id}
          reload_graph={reload_graph}
          disabled={this.state.loading}
          onUpdate={this.handleConfigUpdate}
        />
      </div>
    );
  }
}

class ConfiguratorManager extends React.Component {
  static propTypes = {
    graph_data: PropTypes.object,
    graph_loading: PropTypes.bool,
    reload_graph: PropTypes.func,
    connectable_id: PropTypes.number,
    connectable_type: PropTypes.oneOf(['user','organization'])
  }

  mapSegments = (segment) => (
    <SegmentCard
      key={segment.id}
      connectable_type={this.props.connectable_type}
      connectable_id={this.props.connectable_id}
      reload_graph={this.props.reload_graph}
      {...segment}
    />
  )

  render() {
    const {
      graph_data,
      graph_loading
    } = this.props;

    if (graph_loading) {
      return <h1>{'Loading...'}</h1>;
    }

    return (
      <div className="pairin-admin-body">
        <div className="pairin-admin-segments">
          {
            resolve(graph_data, 'segments', []).map(this.mapSegments)
          }
        </div>
      </div>
    );
  }
}

ConfiguratorManager.parseQueryData = ({ segments=[], user_segments=[], user, organization }, { connectable_type }) => {
  const segment_map = {};
  for (const segment of segments) {
    segment_map[segment.id] = {...segment};
    segment_map[segment.id].config = segment_map[segment.id].config.default;
  }

  for (const user_segment of user_segments) {
    segment_map[user_segment.id].user_enabled = !!resolve(user_segment, 'config/user', null);
    segment_map[user_segment.id].user_config = resolve(user_segment, 'config/user', null);
  }

  for (const org of resolve(user, 'organizations', [])) {
    for (const org_segment of resolve(org, 'segments', [])) {
      segment_map[org_segment.id].org_enabled = !!resolve(org_segment, 'config/organization', null);
      segment_map[org_segment.id].org_config = resolve(org_segment, 'config/organization', null);
    }
  }

  const org_segments = resolve(organization, 'segments', []);

  for (const segment of org_segments) {
    segment_map[segment.id].org_enabled = !!resolve(segment, 'config/organization', null);
    segment_map[segment.id].org_config = resolve(segment, 'config/organization', null);
  }
  
  return {
    segments: Object.values(segment_map)
  };
};

ConfiguratorManager.query = async (token, { connectable_id, connectable_type }) => {
  let connectable_query = '';

  if (connectable_type === 'user') {
    connectable_query = `
      user (id: ${connectable_id}) {
        organizations {
          segments {
            id
            enabled
            config {
              organization
            }
          }
        }
      }
      user_segments: segments (user_id: ${connectable_id}) {
        id,
        enabled,
        config {
          user
        }
      }
    `;
  } else if (connectable_type === 'organization') {
    connectable_query = `
      organization (id: ${connectable_id}) {
        segments {
          id
          enabled
          config {
            organization
          }
        }
      }
    `;
  }

  let q = `
    {
      segments {
        base_path,
        config {
          default
        }
        enabled,
        id,
        title
      }
      ${connectable_query}
    }
  `;

  return await Query(q);
};

export default LoadGraphData(ConfiguratorManager);
